import { useState } from 'react';
import _ from 'lodash';
import { Toggle, Button, SplitButton, SplitButtonMenu, Box, Flex, Loader, MenuItem } from '@vibe/core';
import { Person, BoardPrivate } from '@vibe/icons';
import { usePortal } from '../hooks/use-portal';
import { useClient } from '../hooks/use-client';
import { Accounts } from './Accounts';
import { openPreviewWindow } from '../services/preview';
import { Slots } from './Slots';
import ErrorBox from './ErrorBox';
import { getClientPortalUrl } from '../lib/utils';

function ClientManager() {
  const { portalState, portal, registryEntries, clientBoard } = usePortal();
  const {
    clientSettings,
    setSettingsDraft,
    triggerUpdateClientSettings,
    isUpdatingClientSettings,
    triggerAddAccount,
    triggerUpdateAccount,
    triggerDeleteAccount,
    triggerExport,
    fetchClient,
    slots,
    isExporting,
    clientState,
    triggerImportAccount,
    isImportingAccount,
    mondayContext,
  } = useClient();
  const [view, setView] = useState<'accounts' | 'settings'>('accounts');

  if (portalState.state === 'error') {
    if (['MISSING_INTEGRATION_USER', 'INVALID_INTEGRATION_USER', 'INTEGRATION_USER_NOT_BOARD_OWNER'].includes(portalState.errorKind)) {
      return (
        <ErrorBox
          type="info"
          headline="Client Portal is not set up yet"
          description={`Before you can access client related settings, you must first set up a client portal. Add the "Client Portal Builder" view to this board and follow the steps displayed on screen.`}
        />
      );
    }

    if (portalState.errorKind === 'USER_NOT_OWNER') {
      return (
        <ErrorBox
          headline="You are not a board owner"
          description="You have to be an owner of this board to make changes to client related settings."
        />
      );
    }

    return <ErrorBox headline="Error while fetching portal" description={portalState.error} />;
  }

  if (clientState.state === 'error') {
    return <ErrorBox headline="Error while fetching client settings" description={clientState.error} />;
  }

  if ((portalState.state !== 'ready' && clientState.state !== 'ready') || !clientSettings) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{
          width: '100%',
          height: '100vh',
          textAlign: 'center',
        }}
      >
        <Loader color="primary" size={30} />
      </Flex>
    );
  }

  const portalUrl = portal ? getClientPortalUrl(portal.id, registryEntries) : '';
  const portalName = _.get(portal, 'settings.name') || '';
  const portalGoogleAuthentication = _.get(portal, 'settings.enableGoogleAuth') || false;

  return (
    <>
      <Box>
        <Box className="portalmanager">
          <Box shadow="xs" className="portalmanager__header">
            <Flex
              justify="start"
              align="center"
              direction="row"
              gap={30}
              style={{
                height: 'inherit',
              }}
            >
              <Flex
                style={{
                  //marginLeft: '-15px',
                  width: '200px',
                }}
              >
                <Button
                  kind="tertiary"
                  size="small"
                  disabled={view === 'accounts' ? true : false}
                  leftIcon={Person}
                  onClick={() => {
                    setView('accounts');
                  }}
                >
                  Login methods
                </Button>
                <Button
                  kind="tertiary"
                  size="small"
                  disabled={view === 'settings' ? true : false}
                  leftIcon={BoardPrivate}
                  onClick={() => {
                    setView('settings');
                  }}
                >
                  Dedicated boards
                </Button>
              </Flex>

              <Box
                style={{
                  marginLeft: 'auto',
                }}
              >
                <Flex justify="end" gap={8} style={{ marginRight: '0px' }}>
                  <Button
                    kind="secondary"
                    size="small"
                    color="primary"
                    //leftIcon={clientSettings.enabled ? Unlocked : Locked}
                    onClick={async () => {
                      const value = !clientSettings.enabled;
                      setSettingsDraft({ ...clientSettings, enabled: value });
                      await triggerUpdateClientSettings({ ...clientSettings, enabled: value });
                      await fetchClient();
                      setSettingsDraft(null);
                    }}
                    style={{
                      marginRight: '4px',
                      marginLeft: '0px',
                      paddingLeft: '12px',
                      borderColor: clientSettings.enabled ? '#0073ea' : '#d83a52',
                      color: clientSettings.enabled ? '#0073ea' : '#d83a52',
                    }}
                  >
                    {clientSettings.enabled ? 'Enabled' : 'Disabled'}
                    <Box
                      style={{
                        marginRight: '-8px',
                        marginLeft: '4px',
                        pointerEvents: 'none',
                      }}
                      className={`portalmanager__clienttoggle ${!clientSettings.enabled ? 'disabled' : ''}`}
                    >
                      <Toggle
                        className="portalmanager__clienttoggle-track"
                        offOverrideText=""
                        onOverrideText=""
                        isSelected={!!clientSettings.enabled}
                        onChange={async (value: any) => {
                          setSettingsDraft({ ...clientSettings, enabled: value });
                          await triggerUpdateClientSettings({ ...clientSettings, enabled: value });
                          await fetchClient();
                          setSettingsDraft(null);
                        }}
                        size="small"
                      />
                    </Box>
                  </Button>

                  {!portal?.lastPublishedAt ? (
                    <Button
                      kind="primary"
                      size="small"
                      disabled={!portal?.lastUpdatedAt}
                      onClick={() => {
                        if (clientBoard && mondayContext?.itemId) {
                          openPreviewWindow(clientBoard.id, mondayContext?.itemId, 'draft');
                        }
                      }}
                      style={{
                        paddingLeft: '13px',
                        paddingRight: '13px',
                      }}
                    >
                      Preview draft
                    </Button>
                  ) : (
                    <SplitButton
                      children={'Preview draft'}
                      onClick={() => {
                        if (clientBoard && mondayContext?.itemId) {
                          openPreviewWindow(clientBoard.id, mondayContext?.itemId, 'draft');
                        }
                      }}
                      dialogPaddingSize="none"
                      kind="primary"
                      size="small"
                      color="primary"
                      disabled={!portal?.lastUpdatedAt}
                      shouldCloseOnClickInsideDialog
                      className="custom__splitbutton"
                      secondaryDialogContent={
                        <SplitButtonMenu className="customSplitMenu customSplitMenuAuto">
                          <MenuItem
                            title="Preview published portal"
                            className="customMenuItem"
                            // @ts-ignore
                            disabled={!portal?.lastPublishedAt}
                            onClick={() => {
                              if (clientBoard && mondayContext?.itemId) {
                                openPreviewWindow(clientBoard.id, mondayContext?.itemId, 'public');
                              }
                            }}
                          />
                        </SplitButtonMenu>
                      }
                    />
                  )}
                </Flex>
              </Box>
            </Flex>
          </Box>

          <Box className="portalmanager__content" backgroundColor="greyBackgroundColor">
            {view === 'accounts' ? (
              <Accounts
                portalUrl={portalUrl}
                portalName={portalName}
                portalGoogleAuthentication={portalGoogleAuthentication}
                accounts={clientSettings.accounts}
                // @ts-ignore
                isAdmin={mondayContext?.user.isAdmin || false}
                isExporting={isExporting}
                isImportingAccount={isImportingAccount}
                onCreate={async (account) => {
                  await triggerAddAccount(account);
                  await fetchClient();
                }}
                onUpdate={async (accountUpdate, accountId) => {
                  await triggerUpdateAccount({ accountUpdate, accountId });
                  await fetchClient();
                }}
                onDelete={async (account) => {
                  await triggerDeleteAccount((account as any).id);
                  await fetchClient();
                }}
                onExport={(account) => {
                  triggerExport((account as any).id);
                }}
                onImport={async (accountToImport) => {
                  await triggerImportAccount(accountToImport);
                  await fetchClient();
                }}
              />
            ) : null}
            {view === 'settings' && slots !== undefined ? (
              <Slots
                slots={slots}
                slotSettings={clientSettings.slotSettings}
                onChange={async (newSlotSettings) => {
                  const nextDraftSettings = { ...clientSettings, slotSettings: newSlotSettings };
                  setSettingsDraft(nextDraftSettings);
                  await triggerUpdateClientSettings(nextDraftSettings);
                  await fetchClient();
                  setSettingsDraft(null);
                }}
                isSaving={isUpdatingClientSettings}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default ClientManager;
